import { gql } from '@apollo/client';
import { useQuery, UseQueryResult } from 'react-query';
import { graphqlClient } from 'bk-web-app';
import { GlobalService } from '../types/GlobalService';

const GLOBAL_SERVICES_FOR_DATA_SOURCE_QUERY = gql`
  query GlobalServicesForDataSource($filter: GlobalServiceFilter) {
    globalServices(filter: $filter) {
      id
      dataSource {
        id
        name
      }
      action {
        id
        name
      }
      status
    }
  }
`;

interface GraphQLGlobalService {
  id: string;
  dataSource: { id: string; name: string };
  action: { id: string; name: string };
  status: string;
}

const mapGraphQLGlobalService = (gs: GraphQLGlobalService): GlobalService => ({
  globalServiceId: gs.id,
  dataSourceId: gs.dataSource.id,
  dataSourceName: gs.dataSource.name,
  actionId: gs.action.id,
  actionName: gs.action.name,
  status: gs.status,
});

const get = async (apiUrl: string, dataSourceId: string): Promise<GlobalService[]> => {
  if (!apiUrl || !dataSourceId) {
    throw new Error('Required properties are not set');
  }

  const response: Response = await fetch(`${apiUrl}/data-sources/${dataSourceId}/global-services`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch global services for data source');
  }

  return (await response.json()) as GlobalService[];
};

const getViaGraphQL = async (dataSourceId: string): Promise<GlobalService[]> => {
  const { data } = await graphqlClient.query({
    query: GLOBAL_SERVICES_FOR_DATA_SOURCE_QUERY,
    variables: { filter: { dataSourceId } },
    context: { useV2: true },
  });
  return data.globalServices.map(mapGraphQLGlobalService);
};

interface UseFetchGlobalServicesForDataSourceProps {
  dataSourceId?: string;
  useGraphQL?: boolean;
}

export const useFetchGlobalServicesForDataSource = ({
  dataSourceId,
  useGraphQL = false,
}: UseFetchGlobalServicesForDataSourceProps): UseQueryResult<GlobalService[]> => {
  const apiUrl = window.BENCH?.auApi?.FIND_GLOBAL_SERVICES_API_URL || '';

  return useQuery({
    queryKey: ['getGlobalServicesForDataSource', dataSourceId, useGraphQL],
    enabled: !!dataSourceId,
    queryFn: () => {
      if (!dataSourceId) {
        throw new Error('dataSourceId is required');
      }
      return useGraphQL ? getViaGraphQL(dataSourceId) : get(apiUrl, dataSourceId);
    },
  });
};
